'use client';
import React, { useState } from 'react';
import { useTranslation, useWindowDimensions } from '@/hook';
import {
    CalendarBookedCar2,
    carFrame,
    engine,
    HighlightsIcon,
    Speedometer2,
    TransmissionIcon2,
} from '../../../../../public/Images';
import StatusBadge from '@/components/shared/StatusBadge';

import Modal from '@/components/shared/modal.component';
import {
    CarDetailsType,
    DisplayedCarInfoInterface,
    HighlightsType,
    ImperfectionReportSectionGrouped,
} from '@/types';
import { getCarType, CarTypesEnum } from '../../car-type';
import { PricingAndInstallmentSection } from './PricingAndInstallmentSection';
import { numberWithCommas } from '@/utils';

import NotifyMe from '../NotifyMe';
import {
    CarProfileCarName,
    CarProFileMoreInfoContainer,
    CarProFileMoreInfoItemContainer,
    CarProfileMoreInfoText,
} from '../StyledComponents/car-info-section-components';
import CarSoldOverlay from '../SimilarCars/CarSoldOverlay';
import Image from 'next/image';
import ShowroomSection from './ShowroomSection/ShowroomSection';
import { AVAILABLE_LANGUAGES, colors } from '@/constants';

import CarDetailsSection from '../CarProfileInfo/CarDetailsSection';
import { TFunction } from 'i18next';
import CarCTAs from '../CarCTAs';
import { CarIncludes } from '../CarIncludes/car-includes';
import { CarTypeBannerDesktop } from '../CarTypeBanner/CarTypeBanner';
import { SylndrHelpSection } from '../SylndrHelpSection';
import Box from '@/components/shared/Box/Box';
import { useVerticalOffset } from '@/hook/use-vertical-offset.hook';
import Typography from '@/components/shared/typography/typography.component';
import Button from '@/components/shared/Button/button-styled.component';

const getCarSummaryTitle = (
    carType: CarTypesEnum | undefined,
    t: TFunction
) => {
    return carType === CarTypesEnum.CERTIFIED
        ? t('carSummaryAndWhatsRefurbished')
        : t('carSummary');
};

export const CarSummary = ({
    imperfectionDataGroupped,
    carDetails,
    t,
    carType,
}: {
    imperfectionDataGroupped: ImperfectionReportSectionGrouped;
    carDetails: CarDetailsType;
    t: TFunction;
    carType?: CarTypesEnum;
}) => {
    return (
        <>
            <Typography className="text-gray-800 text-body1SemiBold md:text-subtitle2SemiBold mb-2">
                {getCarSummaryTitle(carType, t)}
            </Typography>
            <div className="border flex flex-wrap border-solid border-gray-100 rounded-lg py-6 px-4 gap-5">
                <div className="flex  gap-x-2 p-x-1 border-r border-solid border-gray-100 w-[calc(33.333%-1.25rem)] items-center last:border-0">
                    <Image className="w-5 h-5" src={carFrame} alt="frame" />
                    {imperfectionDataGroupped?.isRepainted ? (
                        imperfectionDataGroupped?.numberOfRepaintedPanels ==
                        1 ? (
                            <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                                {
                                    imperfectionDataGroupped?.numberOfRepaintedPanels
                                }{' '}
                                {t('repaintedPart')}
                            </div>
                        ) : imperfectionDataGroupped?.numberOfRepaintedPanels >
                          3 ? (
                            <div className="text-gray-600 text-sm font-normal leading-tight">
                                {t('fewRepaintedParts')}
                            </div>
                        ) : (
                            <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                                {
                                    imperfectionDataGroupped?.numberOfRepaintedPanels
                                }{' '}
                                {t('repaintedParts')}
                            </div>
                        )
                    ) : (
                        <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                            {t('noRepaintedParts')}
                        </div>
                    )}
                </div>

                <div className="flex  gap-x-2 p-x-1 border-r border-solid border-gray-100 w-[calc(33.333%-1.25rem)] items-center last:border-0 ">
                    <Image className="w-5 h-5" src={engine} alt="engine" />

                    <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                        {t('noMechProblems')}
                    </div>
                </div>

                {carDetails?.highlights?.ar.map((h, index) => (
                    <div
                        className={`flex  gap-x-2 px-1 ${
                            index % 3 == 0
                                ? ''
                                : 'border-r border-solid border-gray-100'
                        } w-[calc(33.333%-1.25rem)] items-center last:border-0 `}
                        key={`${h} highlight-item`}
                    >
                        <Image
                            className="w-5 h-5"
                            src={HighlightsIcon}
                            alt="star"
                        />
                        <div className="en:hidden text-gray-600 text-sm font-normal leading-tight mt-1">
                            {carDetails.highlights?.ar[index]}
                        </div>
                        <div className="ar:hidden text-gray-600 text-sm font-normal leading-tight">
                            {carDetails.highlights?.en[index]}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};
const MorInfo = ({
    inspectionRef,
    carSummaryRef,
    highlights,
    isRepainted,
    numberOfRepaintedPanels,
    kilometers,
    transmission,
    year,
    carType,
}: {
    inspectionRef: React.RefObject<HTMLDivElement>;
    carSummaryRef: React.RefObject<HTMLDivElement>;
    highlights: HighlightsType;
    isRepainted: boolean;
    numberOfRepaintedPanels: number;
    kilometers: string | number;
    year: number;
    transmission: string;
    carType?: CarTypesEnum;
}) => {
    const { t } = useTranslation();
    const offset = useVerticalOffset(['mobile-car-tabs', 'nav-bar']);
    return (
        <>
            <div className="md:hidden h-[1px] bg-gray-200 -mx-4"></div>
            <div className="pt-6 w-full" ref={carSummaryRef}>
                <Typography className="text-gray-800 text-body1SemiBold md:text-subtitle2SemiBold mb-2">
                    {getCarSummaryTitle(carType, t)}
                </Typography>

                <div className="border border-solid border-gray-100 p-4 pt-5 rounded-lg md:border-0 md:p-0 md:mb-0">
                    <div className="hidden md:block mb-5 md:mb-0">
                        <CarProFileMoreInfoContainer>
                            <CarProFileMoreInfoItemContainer>
                                <Image
                                    src={Speedometer2}
                                    alt="Speedometer"
                                    className="h-5 w-5"
                                />
                                <CarProfileMoreInfoText
                                    variant={'body2'}
                                >{`${numberWithCommas(kilometers)} ${t(
                                    'km'
                                )}`}</CarProfileMoreInfoText>
                            </CarProFileMoreInfoItemContainer>
                            <CarProFileMoreInfoItemContainer>
                                <Image
                                    src={CalendarBookedCar2}
                                    alt="CalendarCardIcon"
                                    className="h-5 w-5"
                                />
                                <CarProfileMoreInfoText variant={'body2'}>
                                    {year}
                                </CarProfileMoreInfoText>
                            </CarProFileMoreInfoItemContainer>
                            <CarProFileMoreInfoItemContainer>
                                <Image
                                    src={TransmissionIcon2}
                                    alt="TransmissionIcon"
                                    className="h-5 w-5"
                                />
                                {/*
          @eslint-disable-next-line @typescript-eslint/ban-ts-comment
          @ts-ignore */}
                                <CarProfileMoreInfoText variant={'body2'}>
                                    {t(transmission)}
                                </CarProfileMoreInfoText>
                            </CarProFileMoreInfoItemContainer>
                        </CarProFileMoreInfoContainer>
                    </div>
                    <div className="md:hidden flex flex-col gap-y-4">
                        <div className="flex gap-x-2 items-center">
                            <Image
                                className="w-5 h-5"
                                src={carFrame}
                                alt="frame"
                            />
                            {isRepainted ? (
                                numberOfRepaintedPanels > 3 ? (
                                    <div className="text-gray-600 text-sm font-normal leading-tight">
                                        {t('fewRepaintedParts')}
                                    </div>
                                ) : numberOfRepaintedPanels === 1 ? (
                                    <div className="text-gray-600 text-sm font-normal leading-tight">
                                        {numberOfRepaintedPanels}{' '}
                                        {t('repaintedPart')}
                                    </div>
                                ) : (
                                    <div className="text-gray-600 text-sm font-normal leading-tight">
                                        {numberOfRepaintedPanels}{' '}
                                        {t('repaintedParts')}
                                    </div>
                                )
                            ) : (
                                <div className="text-gray-600 text-sm font-normal leading-tight">
                                    {t('noRepaintedParts')}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-x-2 items-center">
                            <Image
                                className="w-5 h-5"
                                src={engine}
                                alt="engine"
                            />

                            <div className="text-gray-600 text-sm font-normal leading-tight">
                                {t('noMechProblems')}
                            </div>
                        </div>
                        {highlights?.ar?.map((h, index) => (
                            <div
                                className="flex gap-x-2 items-center"
                                key={`${h}-highlight`}
                            >
                                <Image
                                    className="w-5 h-5"
                                    src={HighlightsIcon}
                                    alt="star"
                                />
                                <div className="en:hidden text-gray-600 text-sm font-normal leading-tight">
                                    {highlights?.ar[index]}
                                </div>
                                <div className="ar:hidden text-gray-600 text-sm font-normal leading-tight">
                                    {highlights?.en[index]}
                                </div>
                            </div>
                        ))}
                        <Button
                            onClick={() => {
                                if (inspectionRef?.current) {
                                    const targetPosition =
                                        inspectionRef?.current?.getBoundingClientRect()
                                            .top +
                                        window.scrollY -
                                        offset;
                                    window.scrollTo({
                                        top: targetPosition,
                                        behavior: 'smooth',
                                    });
                                }
                            }}
                            className="w-full bg-white py-[10px] px-4"
                            style={{
                                border: `1px solid ${colors['gray-300']}`,
                            }}
                        >
                            <Typography
                                variant="body2Medium"
                                className="text-primary-700"
                            >
                                {t('InspectionReport')}
                            </Typography>
                        </Button>
                    </div>
                </div>
            </div>
            <div className="md:hidden h-[1px] bg-gray-200 -mx-4 mt-6"></div>
        </>
    );
};
const CarInfo = ({
    carInfo,
    carDetails,
    IS_DESKTOP,
    highlights,
    isRepainted,
    numberOfRepaintedPanels,
    inspectionRef,
    carDetailsOverview,
    includesRef,
    carSummaryRef,
    priceAndInstallmentRef,
}: {
    highlights: HighlightsType;
    isRepainted: boolean;
    numberOfRepaintedPanels: number;
    inspectionRef: React.RefObject<HTMLDivElement>;
    includesRef: React.RefObject<HTMLDivElement>;
    carSummaryRef: React.RefObject<HTMLDivElement>;
    priceAndInstallmentRef: React.RefObject<HTMLDivElement>;
    carInfo: DisplayedCarInfoInterface;
    carDetails: CarDetailsType;
    IS_DESKTOP: boolean;
    carDetailsOverview: any[];
}) => {
    const { i18n, t } = useTranslation();
    const { width } = useWindowDimensions();
    const lang = i18n.language;
    const isDesktop = width > 850;
    const [notifyMeModal, setNotifyModal] = useState(false);

    // Get car type for ShowroomSection
    const carType = getCarType(carDetails);

    return (
        <>
            <div className="p-0 border-0 gap-2 flex flex-col relative md:min-h-[200px] md:py-6 md:px-4 md:border  md:border-gray-200  md:rounded-2xl md:gap-4 ">
                <div
                    className={`absolute top-0 z-10 ${
                        lang === AVAILABLE_LANGUAGES.en ? 'left-0' : 'right-0'
                    }`}
                >
                    <CarTypeBannerDesktop
                        type={carType}
                        lang={lang as AVAILABLE_LANGUAGES}
                    />
                </div>
                <div className="flex flex-col gap-y-3 md:gap-y-4">
                    <div className="pt-3">
                        <div className="flex items-start justify-between relative flex-col">
                            <div className="flex items-start justify-between relative flex-row place-items-center w-full">
                                <CarProfileCarName
                                    variant={
                                        isDesktop
                                            ? 'subtitle2SemiBold'
                                            : 'body1Medium'
                                    }
                                    component="h1"
                                >{`${carInfo?.make} ${carInfo?.model} ${carInfo?.year}`}</CarProfileCarName>
                                <StatusBadge carData={carInfo} />
                            </div>

                            <CarDetailsSection
                                data={carDetailsOverview}
                                IS_DESKTOP={IS_DESKTOP}
                            />
                        </div>
                    </div>

                    {carInfo.showroom &&
                        !carInfo.isPurchaseInProgress &&
                        !carInfo.isSold && (
                            <ShowroomSection
                                showroom={carInfo.showroom}
                                lang={lang as AVAILABLE_LANGUAGES}
                                isDesktop={isDesktop}
                                carInfo={carInfo}
                                carType={carType}
                                dealerInfo={carDetails.dealerInfo}
                            />
                        )}
                </div>

                <div className="relative flex flex-col md:gap-6">
                    {(carInfo?.isSold || carInfo.isPurchaseInProgress) &&
                        isDesktop && (
                            <div className="mt-8">
                                <CarSoldOverlay
                                    type={
                                        carInfo.isPurchaseInProgress
                                            ? 'PURCHASE_IN_PROGRESS'
                                            : 'SOLD'
                                    }
                                    style={{
                                        marginLeft: '-32px',
                                        marginRight: '-16px',
                                        marginBottom: '-24px',
                                        marginTop: '-16px',
                                    }}
                                />
                            </div>
                        )}
                    {!(carInfo?.isSold || carInfo.isPurchaseInProgress) && (
                        <Box
                            ref={priceAndInstallmentRef}
                            className="pb-6 border-b border-gray-200 md:border-0 md:pb-0 -mx-4 px-4"
                        >
                            <PricingAndInstallmentSection
                                sylndrSellingPrice={carInfo?.sylndrSellingPrice}
                                installmentPrice={carInfo?.installmentPrice}
                                discountPrice={carInfo?.discountPrice}
                                carType={carType}
                                carId={carInfo?.id}
                            />
                        </Box>
                    )}

                    <div className="max-md:border-b max-md:border-gray-200 max-md:-mx-4">
                        <div className="md:hidden pb-6 px-4" data-car-guarantee>
                            <Box ref={includesRef} className="pt-3">
                                <CarIncludes
                                    lang={lang as AVAILABLE_LANGUAGES}
                                    carDetails={carDetails}
                                />
                            </Box>
                            <SylndrHelpSection
                                inspectionRef={inspectionRef}
                                carType={carType}
                                lang={lang as AVAILABLE_LANGUAGES}
                                IS_DESKTOP={IS_DESKTOP}
                            />
                            {carType !== CarTypesEnum.Dealer && (
                                <MorInfo
                                    carSummaryRef={carSummaryRef}
                                    inspectionRef={inspectionRef}
                                    highlights={highlights}
                                    isRepainted={isRepainted}
                                    numberOfRepaintedPanels={
                                        numberOfRepaintedPanels
                                    }
                                    kilometers={carInfo?.kilometers}
                                    year={carInfo?.year}
                                    transmission={carInfo?.transmission}
                                    carType={carType}
                                />
                            )}
                        </div>
                    </div>
                    <div className="md:flex hidden md:flex-col md:gap-6">
                        <CarCTAs
                            carInfo={carInfo}
                            IS_DESKTOP={IS_DESKTOP}
                            t={t}
                        />
                    </div>
                    <Modal
                        open={notifyMeModal}
                        sx={{
                            backdropFilter: 'blur(6px)',
                            borderRadius: '12px',
                        }}
                        setOpen={setNotifyModal}
                    >
                        <NotifyMe
                            setModal={setNotifyModal}
                            carId={carInfo?.id}
                        />
                    </Modal>
                </div>
            </div>
        </>
    );
};
export default CarInfo;
